import React, { useState, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import {
  GlobeAltIcon,
  PhotoIcon,
  PlusIcon,
  TrashIcon,
  PencilIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  DocumentArrowDownIcon,
} from "@heroicons/react/24/outline";
import { updateProject } from "../../../../api/project";
import { toast } from "react-hot-toast";
import { formatPrice, parsePrice } from "../../../../utils/formatPrice";
import EnhancedLibrary from "../../Media/EnhancedLibrary";

// Helper function to format price with exactly two decimal places (same as order-items.js)
const formatPriceWithTwoDecimals = (value) => {
  if (value === "" || value === null || value === undefined) return "";
  const stringValue = String(value).trim();
  
  // If empty after trimming, return empty string
  if (stringValue === "") return "";
  
  // If value contains a comma, ensure it has exactly two decimal places
  if (stringValue.includes(",")) {
    const parts = stringValue.split(",");
    const integerPart = parts[0] || "";
    const decimalPart = parts[1] || "";
    
    // Pad decimal part to 2 digits or truncate if more than 2
    let formattedDecimal = decimalPart.substring(0, 2);
    if (formattedDecimal.length === 1) {
      // If only one digit, pad with zero: "21,2" -> "21,20"
      formattedDecimal = formattedDecimal + "0";
    } else if (formattedDecimal.length === 0) {
      // If no digits after comma, add "00": "21," -> "21,00"
      formattedDecimal = "00";
    }
    
    return `${integerPart},${formattedDecimal}`;
  }
  
  // If no comma and value is a valid number, add ",00": "100" -> "100,00"
  if (/^\d+$/.test(stringValue)) {
    return `${stringValue},00`;
  }
  
  // Otherwise return as is
  return stringValue;
};

// Helper function to strip HTML tags from text
const stripHtml = (html) => {
  if (!html) return "";
  // Create a temporary div element
  const tmp = document.createElement("div");
  tmp.innerHTML = html;
  // Get text content and remove extra whitespace
  return tmp.textContent || tmp.innerText || "";
};

export const WebsiteTab = ({ project, onRefresh }) => {
  const { t } = useTranslation();
  const [formData, setFormData] = useState({
    public_title: "",
    public_description: "",
    show_on_website: false,
    public_contract_sum: "",
    public_main_image_id: null,
    website_content_lines: [],
  });
  const [loading, setLoading] = useState(false);
  const [saveStatus, setSaveStatus] = useState(null); // null, saving, success, error
  const [imagePickerOpen, setImagePickerOpen] = useState(false);
  const [editingLineIndex, setEditingLineIndex] = useState(null);
  const [newLine, setNewLine] = useState({ title: "", value: "", line_type: "info" });

  useEffect(() => {
    // Initialize form data from project
    if (project) {
      // Strip HTML from description if present
      const cleanPublicDescription = project.public_description 
        ? stripHtml(project.public_description) 
        : "";
      const cleanDescription = project.description 
        ? stripHtml(project.description) 
        : "";
      
      // Keep public_contract_sum as raw value - formatting happens on display/blur
      setFormData({
        public_title: project.public_title || "",
        public_description: cleanPublicDescription,
        show_on_website: project.show_on_website || false,
        public_contract_sum: project.public_contract_sum || "",
        public_main_image_id: project.public_main_image_id || null,
        website_content_lines: project.website_content_lines || [],
      });
    }
  }, [project?.id, project?.updated_at]);

  // Get display values with fallback to regular title/description
  const displayTitle = formData.public_title || project?.name || project?.displayTitle || "";
  const displayDescription = formData.public_description || (project?.description ? stripHtml(project.description) : "");

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setSaveStatus("saving");

    try {
      const contentLines = (formData.website_content_lines || []).map((line) => ({
        title: String(line?.title ?? "").trim(),
        value: String(line?.value ?? "").trim(),
        line_type: String(line?.line_type ?? "info") || "info",
      }));

      const payload = {
        id: project.id,
        public_title: formData.public_title,
        public_description: formData.public_description,
        show_on_website: formData.show_on_website,
        public_contract_sum: formData.public_contract_sum,
        public_main_image_id: formData.public_main_image_id,
        website_content_lines: contentLines,
      };

      const response = await updateProject(payload);
      
      // updateProject returns response.data directly, so response is already the data object
      if (response && response.id) {
        setSaveStatus("success");
        if (onRefresh) {
          onRefresh(response);
        }
        setTimeout(() => setSaveStatus(null), 3000);
        toast.success("Website innstillinger lagret");
      } else {
        throw new Error("Failed to save");
      }
    } catch (error) {
      console.error("Error saving website settings:", error);
      setSaveStatus("error");
      toast.error("Feil ved lagring av website innstillinger");
      setTimeout(() => setSaveStatus(null), 3000);
    } finally {
      setLoading(false);
    }
  };

  const handleSelectImage = (document) => {
    handleInputChange("public_main_image_id", document.id);
    setImagePickerOpen(false);
  };

  const handleAddContentLine = () => {
    if (!newLine.title.trim() || !newLine.value.trim()) {
      toast.error("Tittel og verdi må fylles ut");
      return;
    }

    setFormData((prev) => ({
      ...prev,
      website_content_lines: [...prev.website_content_lines, { ...newLine }],
    }));

    setNewLine({ title: "", value: "", line_type: "info" });
  };

  const handleEditContentLine = (index) => {
    const line = formData.website_content_lines[index];
    setNewLine({ ...line });
    setEditingLineIndex(index);
  };

  const handleUpdateContentLine = () => {
    if (!newLine.title.trim() || !newLine.value.trim()) {
      toast.error("Tittel og verdi må fylles ut");
      return;
    }

    setFormData((prev) => {
      const updated = [...prev.website_content_lines];
      updated[editingLineIndex] = { ...newLine };
      return {
        ...prev,
        website_content_lines: updated,
      };
    });

    setNewLine({ title: "", value: "", line_type: "info" });
    setEditingLineIndex(null);
  };

  const handleDeleteContentLine = (index) => {
    if (window.confirm("Er du sikker på at du vil slette denne linjen?")) {
      setFormData((prev) => ({
        ...prev,
        website_content_lines: prev.website_content_lines.filter((_, i) => i !== index),
      }));
    }
  };

  // Find selected image from project
  const selectedImage = React.useMemo(() => {
    if (project?.public_main_image) {
      return { id: project.public_main_image.id, ...project.public_main_image };
    }
    if (formData.public_main_image_id && project?.public_main_image) {
      return project.public_main_image;
    }
    return null;
  }, [project?.public_main_image, formData.public_main_image_id]);
  
  // Get image URL from various possible locations
  const selectedImageUrl = React.useMemo(() => {
    if (!selectedImage) return null;
    
    // Try different paths to get the URL
    const url = selectedImage?.file?.url 
      || selectedImage?.file?.file?.url 
      || selectedImage?.file_url 
      || selectedImage?.url
      || (selectedImage?.file && typeof selectedImage.file === 'object' && selectedImage.file.url);
    
    return url || null;
  }, [selectedImage]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center">
          <GlobeAltIcon className="h-6 w-6 text-gray-400 mr-2" />
          <h3 className="text-lg font-medium text-gray-900">
            {t("project.website_settings") || "Website innstillinger"}
          </h3>
        </div>
        <div className="flex items-center space-x-2">
          {saveStatus === "success" && (
            <div className="flex items-center text-green-600">
              <CheckCircleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm">Lagret</span>
            </div>
          )}
          {saveStatus === "error" && (
            <div className="flex items-center text-red-600">
              <ExclamationTriangleIcon className="h-5 w-5 mr-1" />
              <span className="text-sm">Feil ved lagring</span>
            </div>
          )}
        </div>
      </div>

      {/* Website Settings Form */}
      <div className="bg-white shadow rounded-lg p-6">
        <form onSubmit={handleSubmit} className="space-y-6">
          {/* Show on Website Toggle */}
          <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
            <div>
              <label className="text-sm font-medium text-gray-900">
                {t("project.show_on_website") || "Vis prosjekt på hjemmesiden"}
              </label>
              <p className="text-sm text-gray-500 mt-1">
                {t("project.show_on_website_description") || "Aktiver for å vise prosjektet offentlig på hjemmesiden"}
              </p>
            </div>
            <label className="relative inline-flex items-center cursor-pointer">
              <input
                type="checkbox"
                checked={formData.show_on_website}
                onChange={(e) => handleInputChange("show_on_website", e.target.checked)}
                className="sr-only peer"
              />
              <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-blue-600"></div>
            </label>
          </div>

          {/* Visible Title */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.public_title") || "Synlig tittel"}
            </label>
            <input
              type="text"
              value={formData.public_title}
              onChange={(e) => handleInputChange("public_title", e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={displayTitle || (t("project.public_title_placeholder") || "Tittel som vises på hjemmesiden")}
            />
            {!formData.public_title && (
              <p className="mt-1 text-sm text-gray-500">
                Bruker vanlig tittel: <span className="font-medium">{displayTitle}</span>
              </p>
            )}
          </div>

          {/* Visible Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.public_description") || "Synlig beskrivelse"}
            </label>
            <textarea
              value={formData.public_description}
              onChange={(e) => handleInputChange("public_description", e.target.value)}
              rows={4}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder={displayDescription || (t("project.public_description_placeholder") || "Beskrivelse som vises på hjemmesiden")}
            />
            {!formData.public_description && displayDescription && (
              <p className="mt-1 text-sm text-gray-500">
                Bruker vanlig beskrivelse
              </p>
            )}
          </div>

          {/* Public Contract Sum */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.public_contract_sum") || "Offentlig kontraktsum"}
            </label>
            <div className="relative">
              <input
                type="text"
                value={formData.public_contract_sum}
                onChange={(e) => {
                  // Format input: convert dot to comma and remove non-numeric characters (except comma and dot)
                  let inputValue = e.target.value;
                  if (inputValue) {
                    // Remove all characters except numbers, comma, and dot
                    const cleaned = inputValue.replace(/[^0-9,.]/g, "");
                    // Replace dot with comma for decimal separator
                    inputValue = cleaned.replace(/\./g, ",");
                  }
                  handleInputChange("public_contract_sum", inputValue);
                }}
                onBlur={(e) => {
                  // Format on blur with 2 decimals (same as order-items.js)
                  const value = e.target.value.trim();
                  if (value && !isNaN(parseFloat(value.replace(/[^\d.,-]/g, "")))) {
                    const formatted = formatPriceWithTwoDecimals(value);
                    if (formatted) {
                      handleInputChange("public_contract_sum", formatted);
                    }
                  }
                }}
                onKeyDown={(e) => {
                  // Only allow numbers, comma, and dot (dot will be converted to comma)
                  const allowedKeys = /[0-9,.]/;
                  const isNavigationKey = 
                    e.key === "Backspace" || 
                    e.key === "Delete" || 
                    e.key === "ArrowLeft" || 
                    e.key === "ArrowRight" || 
                    e.key === "Tab" ||
                    e.key === "Enter";
                  
                  if (!allowedKeys.test(e.key) && !isNavigationKey) {
                    e.preventDefault();
                  }
                  // Convert dot to comma immediately
                  if (e.key === ".") {
                    e.preventDefault();
                    const syntheticEvent = {
                      target: {
                        name: "public_contract_sum",
                        value: (e.target.value || "") + ",",
                      },
                    };
                    handleInputChange("public_contract_sum", syntheticEvent.target.value);
                  }
                }}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder={t("project.public_contract_sum_placeholder") || "F.eks. 2500000 eller 2500000,50"}
              />
            </div>
            <p className="mt-1 text-sm text-gray-500">
              {t("project.public_contract_sum_description") || "Kontraktsum som vises offentlig på hjemmesiden. Skriv inn tall eller tekst."}
            </p>
          </div>

          {/* Public Main Image */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.public_main_image") || "Offentlig hovedbilde"}
            </label>
            <div className="flex items-start space-x-4">
              {selectedImageUrl && (
                <div className="relative">
                  <img
                    src={selectedImageUrl}
                    alt="Selected"
                    className="w-32 h-32 object-cover rounded-lg border border-gray-300"
                    onError={(e) => {
                      e.target.src = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='100' height='100'%3E%3Crect fill='%23ddd' width='100' height='100'/%3E%3Ctext fill='%23999' x='50%25' y='50%25' text-anchor='middle' dy='.3em'%3EBilde%3C/text%3E%3C/svg%3E";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => handleInputChange("public_main_image_id", null)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                  >
                    <TrashIcon className="h-4 w-4" />
                  </button>
                </div>
              )}
              {formData.public_main_image_id && !selectedImageUrl && (
                <div className="w-32 h-32 bg-gray-100 rounded-lg border border-gray-300 flex items-center justify-center">
                  <PhotoIcon className="w-8 h-8 text-gray-400" />
                </div>
              )}
              <div className="flex-1">
                <button
                  type="button"
                  onClick={() => setImagePickerOpen(true)}
                  className="inline-flex items-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
                >
                  <PhotoIcon className="h-5 w-5 mr-2" />
                  {selectedImageUrl ? t("project.change_image") || "Bytt bilde" : t("project.select_image") || "Velg bilde"}
                </button>
                <p className="mt-1 text-sm text-gray-500">
                  {t("project.public_main_image_description") || "Bilde som vises på hjemmesiden"}
                </p>
              </div>
            </div>
          </div>

          {/* Content Lines */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              {t("project.website_content_lines") || "Innhold (statistikk/info)"}
            </label>
            
            {/* Add/Edit Content Line Form */}
            <div className="mb-4 p-4 bg-gray-50 rounded-lg space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                <input
                  type="text"
                  value={newLine.title}
                  onChange={(e) => setNewLine({ ...newLine, title: e.target.value })}
                  placeholder={t("project.content_line_title") || "Tittel"}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="text"
                  value={newLine.value}
                  onChange={(e) => setNewLine({ ...newLine, value: e.target.value })}
                  placeholder={t("project.content_line_value") || "Verdi"}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <select
                  value={newLine.line_type}
                  onChange={(e) => setNewLine({ ...newLine, line_type: e.target.value })}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                >
                  <option value="info">{t("project.line_type_info") || "Info"}</option>
                  <option value="statistics">{t("project.line_type_statistics") || "Statistikk"}</option>
                </select>
              </div>
              <button
                type="button"
                onClick={editingLineIndex !== null ? handleUpdateContentLine : handleAddContentLine}
                className="inline-flex items-center px-3 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-blue-600 hover:bg-blue-700"
              >
                {editingLineIndex !== null ? (
                  <>
                    <CheckCircleIcon className="h-4 w-4 mr-1" />
                    {t("project.update_line") || "Oppdater"}
                  </>
                ) : (
                  <>
                    <PlusIcon className="h-4 w-4 mr-1" />
                    {t("project.add_line") || "Legg til"}
                  </>
                )}
              </button>
              {editingLineIndex !== null && (
                <button
                  type="button"
                  onClick={() => {
                    setEditingLineIndex(null);
                    setNewLine({ title: "", value: "", line_type: "info" });
                  }}
                  className="ml-2 inline-flex items-center px-3 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
                >
                  {t("project.cancel") || "Avbryt"}
                </button>
              )}
            </div>

            {/* Content Lines List */}
            <div className="space-y-2">
              {formData.website_content_lines.length === 0 ? (
                <p className="text-sm text-gray-500 text-center py-4">
                  {t("project.no_content_lines") || "Ingen innholdslinjer lagt til"}
                </p>
              ) : (
                formData.website_content_lines.map((line, index) => (
                  <div
                    key={index}
                    className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-3">
                        <span className="font-medium text-gray-900">{line.title}</span>
                        <span className="text-gray-500">:</span>
                        <span className="text-gray-700">{line.value}</span>
                        <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-blue-100 text-blue-800">
                          {line.line_type === "statistics" 
                            ? t("project.line_type_statistics") || "Statistikk"
                            : t("project.line_type_info") || "Info"}
                        </span>
                      </div>
                    </div>
                    <div className="flex items-center space-x-2">
                      <button
                        type="button"
                        onClick={() => handleEditContentLine(index)}
                        className="p-1 text-gray-400 hover:text-blue-600"
                      >
                        <PencilIcon className="h-4 w-4" />
                      </button>
                      <button
                        type="button"
                        onClick={() => handleDeleteContentLine(index)}
                        className="p-1 text-gray-400 hover:text-red-600"
                      >
                        <TrashIcon className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>

          {/* Save Button */}
          <div className="flex justify-end pt-6 border-t border-gray-200">
            <button
              type="submit"
              disabled={loading}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  {t("project.saving") || "Lagrer..."}
                </>
              ) : (
                <>
                  <DocumentArrowDownIcon className="h-4 w-4 mr-2" />
                  {t("project.save_settings") || "Lagre innstillinger"}
                </>
              )}
            </button>
          </div>
        </form>
      </div>

      {/* Image Picker Dialog */}
      {imagePickerOpen && (
        <EnhancedLibrary
          module="project"
          moduleParam={project?.id}
          dialogOpen={imagePickerOpen}
          onDialogClose={() => setImagePickerOpen(false)}
          handleDocument={handleSelectImage}
          selected={selectedImage}
        />
      )}
    </div>
  );
};


